import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// API service for Centro backend
class CentroAPI {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  /**
   * Register a new user
   * @param {string} userId - Institution ID
   * @param {string} password - User password
   * @returns {Promise<{success: boolean, userId?: string, error?: string}>}
   */
  async register(userId, password) {
    try {
      const response = await this.client.post('/auth/register', { userId, password });
      return response.data;
    } catch (error) {
      if (error.response) {
        return { success: false, error: error.response.data.error || 'Registration failed' };
      }
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  /**
   * Login user
   * @param {string} userId - Institution ID
   * @param {string} password - User password
   * @returns {Promise<{success: boolean, userId?: string, balance?: number, error?: string}>}
   */
  async login(userId, password) {
    try {
      const response = await this.client.post('/auth/login', { userId, password });
      return response.data;
    } catch (error) {
      if (error.response) {
        return { success: false, error: error.response.data.error || 'Login failed' };
      }
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  /**
   * Check if user exists
   * @param {string} userId - Institution ID
   * @returns {Promise<{exists: boolean}>}
   */
  async checkUser(userId) {
    try {
      const response = await this.client.get(`/auth/check/${userId}`);
      return response.data;
    } catch (error) {
      return { exists: false };
    }
  }

  /**
   * Create a new agreement with escrow
   * @param {string} payerId - ID of the payer
   * @param {string} payeeId - ID of the payee
   * @param {string} thirdPersonId - ID of the third person who can trigger payment
   * @param {number} amount - Amount in Centro
   * @param {number} time - Time period in seconds before hold expires
   * @returns {Promise<{agreementId: string}>}
   */
  async createAgreement(payerId, payeeId, thirdPersonId, amount, time) {
    try {
      const response = await this.client.post('/agreements/create', {
        payerId,
        payeeId,
        thirdPersonId,
        amount,
        time,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Validate and trigger an agreement (must be called by the third person)
   * @param {string} agreementId - ID of the agreement
   * @param {string} thirdPersonId - ID of the third person validating the agreement
   * @returns {Promise<boolean>}
   */
  async agreementMet(agreementId, thirdPersonId) {
    try {
      const response = await this.client.post(`/agreements/${agreementId}/met`, {
        thirdPersonId,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get agreement details
   * @param {string} agreementId - ID of the agreement
   * @returns {Promise<Object>}
   */
  async getAgreement(agreementId) {
    try {
      const response = await this.client.get(`/agreements/${agreementId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get current user's Centro balance
   * @param {string} userId - ID of the user
   * @returns {Promise<{balance: number}>}
   */
  async getBalance(userId) {
    try {
      const response = await this.client.get(`/balance/${userId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Add Centro to user's account
   * @param {string} userId - ID of the user
   * @param {number} amount - Amount to add
   * @returns {Promise<{success: boolean}>}
   */
  async addCentro(userId, amount) {
    try {
      const response = await this.client.post('/centro/add', {
        userId,
        amount,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get all agreements for a user
   * @param {string} userId - ID of the user
   * @returns {Promise<Array>}
   */
  async getUserAgreements(userId) {
    try {
      const response = await this.client.get(`/agreements/user/${userId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Accept/Approve a pending agreement
   * @param {string} agreementId - ID of the agreement
   * @param {string} userId - ID of the user accepting
   * @returns {Promise<Object>}
   */
  async acceptAgreement(agreementId, userId) {
    try {
      const response = await this.client.post(`/agreements/${agreementId}/accept`, {
        userId,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get transaction proof for an agreement
   * @param {string} agreementId - ID of the agreement
   * @returns {Promise<Object>} Proof object containing block hash, validation code, endorsements, etc.
   */
  async getTransactionProof(agreementId) {
    try {
      const response = await this.client.get(`/agreements/${agreementId}/proof`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getSignedReceipt(agreementId) {
    try {
      const response = await this.client.get(`/agreements/${agreementId}/receipt`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  handleError(error) {
    if (error.response) {
      return new Error(error.response.data.message || 'An error occurred');
    } else if (error.request) {
      return new Error('No response from server. Please check if the backend is running.');
    } else {
      return new Error(error.message || 'An unexpected error occurred');
    }
  }
}

export default new CentroAPI();
